import React from 'react';

import { createRoot } from 'react-dom/client';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { i18n } from '@lingui/core';
import {
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductAd } from './ads/SponsoredProductAd.js';
import {
  callMartailerApi,
  type MartailerPayload,
  type MartailerProduct
} from './utils/MartailerApi.js';

const martailerRequest = async (
  container: PlacementId,
  searchQuery: string
) => {
  updatePlacementKeyValueById(container, 'status', 'requested');
  updatePlacementKeyValueById(container, 'creativeType', 'native');
  updateMetricByKey(container, PlacementStatus.AdLoaded);

  try {
    const payload: MartailerPayload = {
      placement: 'search',
      visitorId: 'test-user',
      store: 'finn_recommerce',
      sponsoredProductsCount: 1,
      sponsoredListingsCount: 1,
      searchTerm: searchQuery
    };

    const data = await callMartailerApi(payload);
    console.log('Martailer API response:', data);
    updatePlacementKeyValueById(container, 'status', 'loaded');
    renderSponsoredListingAd(container, data.sponsoredProducts);
  } catch (error) {
    console.log('Error in Martailer request:', error);
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
  }
};

export function triggerMartailerRequest(
  container: PlacementId,
  searchQuery: string | string[] | undefined
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  if (!normalizedQuery) {
    console.log('No search query provided for Martailer request, skipping.');
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
    return;
  }
  void martailerRequest(container, normalizedQuery);
}

function renderSponsoredListingAd(
  placementId: PlacementId,
  products?: Array<MartailerProduct>
) {
  setTimeout(() => {
    const slotElement = document.getElementById(placementId);
    if (!slotElement) {
      console.log('Slot element not found:', placementId);
      return;
    }

    // Clear the slot
    slotElement.innerHTML = '';
    slotElement.style.gridColumn = '1 / 1';

    // Handle no-fill case
    if (!products || products.length === 0) {
      console.log('handle no-fill case for placement:', placementId);
      updateMetricByKey(placementId, PlacementStatus.AdNoFill);
      updatePlacementKeyValueById(placementId, 'status', 'error');
      return;
    }

    const originalLocale = i18n.locale;
    const supportedLocale = ['fi', 'nb', 'da'].includes(i18n.locale)
      ? i18n.locale
      : 'nb'; // Default to Norwegian

    if (originalLocale !== supportedLocale) {
      i18n.activate(supportedLocale);
    }

    // Create a container for the React component
    const container = document.createElement('div');
    slotElement.appendChild(container);

    // Render the React component into the container
    const root = createRoot(container);
    root.render(<SponsoredProductAd layout="grid" products={products} />);

    if (originalLocale !== supportedLocale) {
      setTimeout(() => {
        i18n.activate(originalLocale);
      }, 0);
    }
  }, 1);
}
